import { config } from 'src/lib/config';

type CultureGroup = Models.IngestConfigApi.SetsConfigs.CultureGroup;

export interface LocaleToken {
    type: 'region' | 'culture';
    label: string;
}

export const MAX_LOCALES_SHOWN = 2;

// A region collapses into one label only when it has at least this many cultures.
// A single-culture region (e.g. India) reads clearer as the culture code itself.
const MIN_REGION_SIZE = 2;

const getGroupedLocales = (): Record<string, string[]> => (
    (config as unknown as { groupedLocales?: Record<string, string[]> }).groupedLocales ?? {}
);

export const flattenLocales = (cultureGroups: CultureGroup[]): string[] => (
    [...new Set(cultureGroups?.flatMap((group) => group.cultures))]
);

// Collapses any fully-covered region into a single region token, then lists the leftover cultures.
// Regions come first, so a config covering all of a region reads "North America" at a glance rather
// than four culture codes. Tokens are typed so the cell can style regions distinctly from cultures.
export const summarizeLocaleTokens = (cultureGroups: CultureGroup[]): LocaleToken[] => {
    const cultures = new Set(flattenLocales(cultureGroups));

    if (cultures.size === 0) {
        return [];
    }

    const consumed = new Set<string>();
    const regionTokens: LocaleToken[] = [];

    Object.entries(getGroupedLocales()).forEach(([region, regionCultures]) => {
        const coversRegion = regionCultures.length >= MIN_REGION_SIZE
            && regionCultures.every((culture) => cultures.has(culture));

        if (coversRegion) {
            regionTokens.push({ type: 'region', label: region });
            regionCultures.forEach((culture) => consumed.add(culture));
        }
    });

    const cultureTokens: LocaleToken[] = [...cultures]
        .filter((culture) => !consumed.has(culture))
        .map((culture) => ({ type: 'culture', label: culture }));

    return [...regionTokens, ...cultureTokens];
};
